<script lang="ts">
    import vm from '@cwd/Journeys/Journeys.viewmodel';
    import { api } from '@cwd/lib/main';
    import { dateFormat, deepClone } from '@cwd/shared/utils';
    import cronstrue from 'cronstrue/i18n';
    import { onMounted, onUnmounted, reactive } from 'vue';
    import { JourneyStatus, NodeType } from '@journeys/Enums';
    import { RecurringEditorState, TaskSchedule, TaskScheduleController } from './CronProperties';
    import FluidButton from '@shared/components/FluidButton.vue';
    import Notification from '@shared/components/Alerts/Notification.vue';
    import CronEditor from './CronEditor.vue';
    import UpcomingDatesList from './UpcomingDatesList.vue';
    import { formatDateTime } from '@shared/utils/formatDateTime';

    const component = {
        name: 'RecurringEditor',
    };
    export default {
        name: component.name,
    };
</script>

<script setup lang="ts">
    const props = withDefaults(
        defineProps<{
            start?: Date | string | null;
            ownerId: string;
            ownerName?: string;
            steps: any;
            scheduleCallback?: Func;
            handleSchedule: Func;
            objectType?: string;
            resendToExistingRecipients?: boolean;
            scheduleController: TaskScheduleController;
            disabled?: boolean;
            showExecutionHistoryButton?: boolean;
            disableScheduleCallback?: Func;
            endScheduleCallback?: Func;
            scheduleFailCallback?: Func;
        }>(),
        {
            disabled: false,
            objectType: 'SendIssue',
            ownerName: 'Untitled',
            scheduleCallback: () => console.warn(`Schedule method not defined in ${component.name}`),
            scheduleFailCallback: () => console.warn(`Schedule fail method not defined in ${component.name}`),
        }
    );

    const { editor, activeJourney, account, toast } = vm,
        { events } = editor;

    const emit = defineEmits<{
        (ev: 'update:start', value: Date | string | null): void;
        (ev: 'openExecutionHistory'): void;
    }>();

    onMounted(async () => {
        if (activeJourney.scheduleController?.startingStep && activeJourney.steps[activeJourney.scheduleController?.startingStep])
            await loadCurrentSchedules([activeJourney.steps[activeJourney.scheduleController?.startingStep].data.IssueId!]);
    });

    const state = reactive<RecurringEditorState>({
        showEditor: false,
        displayInactiveTasks: false,
        cronExpression: '',
        nextDates: [],
        schedules: [],
        selectedTaskSchedule: {},
        objectId: '',
        objectType: '',
        resendToExistingRecipients: false,
    });

    const getTaskSchedule = (schedule?: TaskSchedule): TaskSchedule => {
        const {
            TaskType = 'SendIssue',
            ObjectId = '',
            TaskId = '',
            Scheduled = props.start,
            CronExpression = '',
            TimeZone = props.scheduleController.TimeZone,
            TaskStatus = 'Scheduled',
        } = schedule!;

        return {
            TaskId: TaskId,
            ObjectId: ObjectId,
            CronExpression: CronExpression,
            Scheduled: Scheduled || null,
            TaskType: TaskType,
            IsRecurring: true,
            TaskStatus: TaskStatus,
            TimeZone: TimeZone,
        };
    };

    const loadTaskSchedule = (objectId: string) => api.get(`/Scheduling/${objectId}/Schedules`);

    const loadCurrentSchedules = (issueIds: string[]) => {
        const promises: Promise<any>[] = [];

        for (let id of issueIds) promises.push(loadTaskSchedule(id));

        return new Promise((res: any, rej: any) => {
            Promise.all(promises)
                .then((data: any) => {
                    state.schedules = data.flat();

                    res(data);
                })
                .catch((err: any) => {
                    console.error('Failed fetching Schedules:', err);
                    rej(err);
                });
        });
    };

    const loadStepSchedules = (objectIds: string[]) => {
        return api
            .post(`/Scheduling/Schedules`, objectIds)
            .then((res: any) => {
                console.log(res);
            })
            .catch((reason: any) => {
                toast.emit('note', {
                    pos: 'top-left',
                    type: 'error',
                    message: {
                        head: `There were problems fetching step schedules.`,
                        body: 'An error occured. <br>' + '<strong>' + reason.status + '</strong> ' + reason.statusText,
                        break: true,
                    },
                });
            });
    };

    /** delay is always in days, check for placement of '?'
     * to add to either days of month or days of week */
    const addDelayToCron = (expression: string, delay: number) => {
        let split_ex = expression.split(' ');

        if (/\?/g.test(split_ex[3])) {
            31 % delay;
        }
    };

    const setScheduleProps = (schedule: TaskSchedule) => {
        props.scheduleController.Scheduled = schedule.Scheduled!;
        props.scheduleController.TaskStatus = schedule.TaskStatus!;
        props.scheduleController.IsRecurring = schedule.IsRecurring!;
        props.scheduleController.CronExpression = schedule.CronExpression!;
        props.scheduleController.TimeZone = schedule.TimeZone!;
    };

    const saveSchedule = (schedule: TaskSchedule & { [k: string]: any }) => {
        schedule.IsRecurring = true;
        schedule.TaskStatus = 'Scheduled';

        const steps = Object.values(activeJourney.steps).map((step: any) => {
            let data: {
                [k: string]: any;
                Schedule: TaskSchedule;
            } = deepClone(step.data);

            data.Name = activeJourney.Title + ' - ' + data.Name;
            data.SerializedExpression = JSON.stringify({
                SegmentId: data.SegmentId,
                Name: '[JS] - ' + data.Name,
                Description: $Resources.TaskCreatedByLbl + ' journey: ' + props.ownerName,
                SegmentDefinition: data.SegmentDefinition,
            });
            data.Schedule = deepClone<TaskSchedule>(schedule);
            data.Schedule.CronExpression = activeJourney.scheduleController.CronExpression;
            data.Schedule.TimeZone = activeJourney.scheduleController.TimeZone;
            data.Schedule.ObjectId = data.IssueId || data.SmsMessageId || null;
            data.Schedule.TaskId = data.TaskId;
            data.Schedule.Scheduled = activeJourney.scheduleController.Scheduled;
            data.NodeId = step.nodeId;
            data.Delay = step.delay;

            delete data.SegmentDefinition;
            return data;
        });

        api.post(`/journeys/${props.ownerId}/Publish`, steps)
            .then((response: any) => {
                props.scheduleController.startingStep = activeJourney.startingStep;

                for (let i = 0; i < response.length; i++) {
                    if (props.scheduleController.startingStep === response[i].NodeId && response[i].Schedule)
                        setScheduleProps(response[i].Schedule);

                    if (response[i].TaskId)
                        props.scheduleController.activeTasks[response[i].TaskId] = response[i];

                    if (response[i].IssueListing)
                        account.listings.addListing('/issues/listing', response[i].IssueListing);
                    if (response[i].SegmentListing) {
                        account.listings.addListing('/segments', response[i].SegmentListing);
                        activeJourney.ownedObjects.segments[response[i].SegmentListing.SegmentId] ||=
                            response[i].SegmentListing.SegmentId;
                    }
                    if (response[i].TaskId) activeJourney.ownedObjects.tasks[response[i].TaskId] ||= response[i].TaskId;
                    if (response[i].IssueId) activeJourney.ownedObjects.issues[response[i].IssueId] ||= response[i].IssueId;
                    activeJourney.Status = JourneyStatus.Active;
                }

                props.scheduleCallback(response, loadStepSchedules);
            })
            .catch((reason: any) => {
                // Properly restore the safeguarded state while maintaining Vue reactivity
                const safeguard = editor.history.cache.safeguard;
                if (safeguard) {
                    // Clear and restore nodes
                    for (const key in activeJourney.nodes) {
                        delete activeJourney.nodes[key];
                    }
                    for (const key in safeguard.nodes) {
                        activeJourney.nodes[key] = safeguard.nodes[key];
                        // Ensure non-text nodes have a valid runtime object (functions don't clone properly)
                        if (activeJourney.nodes[key].data.type !== NodeType.Text) {
                            (activeJourney.nodes[key] as any).runtime ||= {};
                        }
                    }

                    // Clear and restore lines
                    for (const key in activeJourney.lines) {
                        delete activeJourney.lines[key];
                    }
                    for (const key in safeguard.lines) {
                        activeJourney.lines[key] = safeguard.lines[key];
                    }

                    // Restore other properties
                    activeJourney.Status = safeguard.Status;
                    activeJourney.startingNode = safeguard.startingNode;
                    activeJourney.startingStep = safeguard.startingStep;
                    activeJourney.itm_count = safeguard.itm_count;

                    // Reinitialize all lines to restore their runtime computed properties
                    for (const key in activeJourney.lines) {
                        editor.events.emit('initline', activeJourney.lines[key]);
                    }

                    // Clear selection to avoid stale references to old node/line objects
                    editor.events.emit('clearselection');
                }

                let errorMessage = 'Unknown error';

                try {
                    if (reason.message != null) {
                        const parsed = JSON.parse(reason.message);
                        errorMessage = parsed.Message || parsed.message || reason.message || 'Unknown error';
                    } else if (reason.statusText) {
                        errorMessage = reason.statusText;
                    }
                } catch (e) {
                    errorMessage = reason.message || reason.statusText || 'Unknown error';
                }

                toast.emit('note', {
                    pos: 'top-left',
                    type: 'error',
                    message: {
                        head: $Resources.JourneyPublishFailedTitle,
                        body: $Resources.JourneyPublishFailedBody + ' <br>' + '<strong>' + (reason.statusText || 'Error') + '</strong> ' + errorMessage,
                        break: true,
                    },
                });

                props.scheduleFailCallback();
            });
    };

    const saveSelectedSchedule = async (schedule: TaskSchedule = state.selectedTaskSchedule) => {
        await setState('save');
        return saveSchedule(schedule as TaskSchedule);
    };

    const saveSelectedScheduleHandler = () => saveSelectedSchedule(state.selectedTaskSchedule);

    events.addListener('re_saveselectedschedule', saveSelectedScheduleHandler);

    onUnmounted(() => {
        events.removeListener('re_saveselectedschedule');
        events.removeListener('re_croneditor');
        events.removeListener('re_disableactiveschedule');
    });

    // TODO: reschedule
    const cache: any = {
        schedule: {},
    };
    const setState = async (action: string | null = null, schedule?: TaskSchedule) => {
        // complete reset
        if (action === 'reset') {
            emit('update:start', '');
            state.selectedTaskSchedule = {};
            state.showEditor = true;
        }

        // current state options
        if (action === 'edit') {
            state.selectedTaskSchedule = getTaskSchedule(schedule);
            cache.schedule = state.selectedTaskSchedule;
            emit('update:start', (state.selectedTaskSchedule?.Scheduled as Date) || '');

            state.showEditor = true;
        }

        // empty template schedule
        if (!action) {
            state.selectedTaskSchedule = getTaskSchedule();

            state.showEditor = true;
        }

        // close and revert to cache
        if (action === 'close') {
            emit('update:start', (cache.schedule?.Scheduled as Date) || '');
            state.selectedTaskSchedule = cache.schedule;

            state.showEditor = false;
        }

        // update on close
        if (action === 'save') {
            await updateCurrentDate();

            state.showEditor = false;
        }
    };

    const cronEditorHandler = setState;

    events.addListener('re_croneditor', cronEditorHandler);

    const updateCurrentDate = async () => {
        if (!state.nextDates || Object.keys(state.nextDates).length === 0) {
            await api.get(
                '/CronExpressions/GetFutureDates?cronExpression=' +
                    encodeURIComponent(activeJourney.scheduleController.CronExpression) +
                    '&timeZone=' +
                    activeJourney.scheduleController.TimeZone +
                    '&count=5&start=' +
                    encodeURIComponent(dateFormat('yyyy-mm-dd', activeJourney.scheduleController.Scheduled)),
                null,
                state.nextDates
            );
        }

        if (state.nextDates?.length > 0) {
            if (state.selectedTaskSchedule) {
                state.selectedTaskSchedule.Scheduled = state.nextDates[0];
            } else {
                state.selectedTaskSchedule = { Scheduled: state.nextDates[0] };
            }
        }

        if (state.selectedTaskSchedule?.Scheduled !== undefined && state.selectedTaskSchedule?.Scheduled !== null) {
            emit('update:start', state.selectedTaskSchedule?.Scheduled);
        }
    };

    const disable = (callback?: Func) => {
        const taskIds = Object.values(props.scheduleController.activeTasks).map((task: any) => task.TaskId);

        api.post(`/journeys/Schedule/Stop`, taskIds).then((res: any) => {
            props.scheduleController.TaskStatus = 'Aborted';

            for (let i = 0; i < state.schedules.length; i++) state.schedules[i].TaskStatus = 'Aborted';

            props.scheduleCallback(null, loadStepSchedules);

            state.selectedTaskSchedule = {
                TimeZone: '',
            };

            state.showEditor = false;
            callback && callback();
        });
    };

    const end = (callback?: Func) => {
        const taskIds = Object.values(props.scheduleController.activeTasks).map((task: any) => task.TaskId);

        api.post(`/journeys/Schedule/End`, taskIds).then((res: any) => {
            props.scheduleController.TaskStatus = 'Ended';

            for (let i = 0; i < state.schedules.length; i++) state.schedules[i].TaskStatus = 'Ended';

            props.scheduleCallback(null, loadStepSchedules, 'end');

            state.selectedTaskSchedule = {
                TimeZone: '',
            };

            state.showEditor = false;
            callback && callback();
        });
    };

    const disableActiveScheduleHandler = () => disable(props.disableScheduleCallback);
    const endActiveScheduleHandler = () => end(props.endScheduleCallback);

    events.addListener('re_disableactiveschedule', disableActiveScheduleHandler);
    events.addListener('re_endactiveschedule', endActiveScheduleHandler);

    onUnmounted(() => {
        events.removeListener('re_disableactiveschedule');
        events.removeListener('re_endactiveschedule');
    });

    let cronstrueOptions = { locale: appconfig.lang.loc };

    // A step (n/n) in the minute or hour field means the schedule repeats within a day (hourly/minutely),
    // so the start time-of-day positions the upcoming-dates list rather than being encoded in the cron.
    const isIntervalSchedule = (cron?: string | null) => {
        if (!cron) return false;
        const parts = cron.split(' ');
        return /\//.test(parts[1] ?? '') || /\//.test(parts[2] ?? '');
    };
</script>

<template>
    <template v-if="!state.showEditor">
        <p v-if="activeJourney.scheduleController.Scheduled">
            <span class="label-look d-block">{{ $Resources.RecuringStartDateLabel }}:</span>
            <strong>{{ formatDateTime(activeJourney.scheduleController.Scheduled) }}</strong>
        </p>

        <p v-if="activeJourney.scheduleController.CronExpression">
            <span class="label-look d-block">{{ $Resources.RecuringCronExpressionLabel }}:</span>
            <strong>{{ cronstrue.toString(activeJourney.scheduleController.CronExpression, cronstrueOptions) }}</strong>
        </p>

        <Notification v-if="disabled" type="info" class="static" :message="{ body: $Resources.StopJourneyToRescheduleInfo }" sizing="small" />

        <div class="form-group" v-if="!disabled">
            <FluidButton
                class="btn btn-default"
                :disabled="disabled"
                :class="state.showEditor && 'idle'"
                :text="!activeJourney.scheduleController.Scheduled ? $Resources.SetScheduleLabel : $Resources.EditScheduleLabel"
                @click="handleSchedule" />
        </div>

        <UpcomingDatesList
            :cronExpression="activeJourney.scheduleController.CronExpression"
            :timeZone="activeJourney.scheduleController.TimeZone"
            :start="activeJourney.scheduleController.Scheduled"
            :includeStartTime="isIntervalSchedule(activeJourney.scheduleController.CronExpression)"
            :showExecutionHistoryButton="showExecutionHistoryButton"
            @openExecutionHistory="$emit('openExecutionHistory')" />
    </template>

    <template v-if="state.showEditor">
        <CronEditor
            v-model:cronExpression="activeJourney.scheduleController.CronExpression"
            v-model:timeZone="activeJourney.scheduleController.TimeZone"
            v-model:selectedStart="activeJourney.scheduleController.Scheduled"
            v-model:nextDates="state.nextDates"
            :showExecutionHistoryButton="showExecutionHistoryButton"
            @openExecutionHistory="$emit('openExecutionHistory')" />
    </template>
</template>

<style>
    .btn.idle {
        pointer-events: none;
    }
</style>
